import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { StarIcon, ShoppingCartIcon, HeartIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

import api from '../services/api';
import { useCart } from '../context/CartContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  // جلب تفاصيل المنتج
  const { data: product, isLoading } = useQuery(
    ['product', id],
    () => api.get(`/products/${id}`).then(res => res.data.data)
  );

  // جلب تقييمات المنتج
  const { data: reviewsData } = useQuery(
    ['product-reviews', id],
    () => api.get(`/products/${id}/reviews`).then(res => res.data.data),
    { enabled: !!id }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            المنتج غير موجود
          </h2>
          <Link to="/products" className="btn-primary">
            العودة للمنتجات
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  const isOutOfStock = product.track_quantity && product.quantity === 0;
  const isLowStock = product.track_quantity && 
    product.quantity <= (product.low_stock_threshold || 5);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Product Images */}
        <div>
          <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-200 mb-4">
            <img
              src={product.images?.[selectedImage]?.url || '/placeholder-product.jpg'}
              alt={product.name}
              className="w-full h-96 object-cover object-center"
            />
          </div>
          
          {product.images && product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg ${
                    selectedImage === index ? 'ring-2 ring-primary-500' : ''
                  }`}
                >
                  <img
                    src={image.url}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-20 object-cover object-center"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {product.name}
          </h1>

          {/* Vendor Info */}
          {product.vendor && (
            <div className="mb-4">
              <Link
                to={`/store/${product.vendor.store_slug}`}
                className="flex items-center text-primary-600 hover:text-primary-700"
              >
                {product.vendor.logo_url && (
                  <img
                    src={product.vendor.logo_url}
                    alt={product.vendor.store_name}
                    className="w-8 h-8 rounded-full object-cover ml-2"
                  />
                )}
                <span className="font-medium">{product.vendor.store_name}</span>
              </Link>
            </div>
          )}

          {/* Rating */}
          {product.rating_count > 0 && (
            <div className="flex items-center mb-4">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarIcon
                    key={star}
                    className={`w-5 h-5 ${
                      star <= parseFloat(product.rating_average || 0)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="mr-2 text-sm text-gray-600">
                {product.rating_average ? parseFloat(product.rating_average).toFixed(1) : '0.0'} ({product.rating_count} تقييم)
              </span>
            </div>
          )}

          {/* Price */}
          <div className="mb-6">
            <div className="flex items-center space-x-4 space-x-reverse">
              <span className="text-3xl font-bold text-primary-600">
                {product.price ? parseFloat(product.price).toLocaleString() : '0'} جنيه
              </span>
              {product.compare_price && (
                <span className="text-xl text-gray-500 line-through">
                  {parseFloat(product.compare_price).toLocaleString()} جنيه
                </span>
              )}
            </div>
            {product.compare_price && (
              <span className="text-sm text-green-600 font-medium">
                وفر {((parseFloat(product.compare_price) - parseFloat(product.price)) / parseFloat(product.compare_price) * 100).toFixed(0)}%
              </span>
            )}
          </div>

          {/* Stock Status */}
          <div className="mb-6">
            {isOutOfStock ? (
              <span className="inline-flex px-3 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full">
                نفد من المخزون
              </span>
            ) : isLowStock ? (
              <span className="inline-flex px-3 py-1 text-sm font-medium bg-orange-100 text-orange-800 rounded-full">
                كمية محدودة ({product.quantity} متبقي)
              </span>
            ) : (
              <span className="inline-flex px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
                متوفر
              </span>
            )}
          </div>

          {/* Quantity Selector */}
          {!isOutOfStock && (
            <div className="mb-6">
              <label className="form-label">الكمية</label>
              <div className="flex items-center space-x-3 space-x-reverse">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  -
                </button>
                <span className="text-lg font-medium w-12 text-center">
                  {quantity}
                </span>
                <button
                  onClick={() => {
                    const maxQty = product.track_quantity 
                      ? product.quantity 
                      : quantity + 1;
                    setQuantity(Math.min(maxQty, quantity + 1));
                  }}
                  className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  +
                </button>
              </div>
            </div>
          )}

          {/* Add to Cart Button */}
          <div className="flex space-x-4 space-x-reverse mb-8">
            <button
              onClick={handleAddToCart}
              disabled={isOutOfStock}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ShoppingCartIcon className="w-5 h-5 ml-2" />
              {isOutOfStock ? 'نفد من المخزون' : 'إضافة للسلة'}
            </button>
            <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50">
              <HeartIcon className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Product Details */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              تفاصيل المنتج
            </h3>
            <div className="prose prose-sm text-gray-600">
              <p>{product.description}</p>
            </div>

            {/* Specifications */}
            {product.specifications && product.specifications.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-3">المواصفات</h4>
                <dl className="grid grid-cols-1 gap-2">
                  {product.specifications.map((spec, index) => (
                    <div key={index} className="flex justify-between py-2 border-b border-gray-100">
                      <dt className="font-medium text-gray-900">{spec.name}</dt>
                      <dd className="text-gray-600">{spec.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {/* Shipping Info */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">معلومات الشحن</h4>
              <div className="text-sm text-gray-600 space-y-1">
                {product.shipping_free ? (
                  <p>✓ شحن مجاني</p>
                ) : (
                  <p>تكلفة الشحن: {product.shipping_cost || 0} جنيه</p>
                )}
                <p>وقت التجهيز: {product.processing_time || '1-2 أيام عمل'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      {reviewsData && reviewsData.data && reviewsData.data.length > 0 && (
        <div className="mt-12 border-t pt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            التقييمات ({reviewsData.total || reviewsData.data.length})
          </h2>
          <div className="space-y-6">
            {reviewsData.data.map((review) => (
              <div key={review.id} className="border-b pb-6 last:border-b-0">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">
                        {review.user?.name || 'مستخدم'}
                      </span>
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <StarIcon
                            key={star}
                            className={`w-4 h-4 ${
                              star <= review.rating
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    {review.title && (
                      <h4 className="font-medium text-gray-900 mb-1">
                        {review.title}
                      </h4>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">
                    {new Date(review.created_at).toLocaleDateString('ar-SA')}
                  </span>
                </div>
                {review.comment && (
                  <p className="text-gray-600 text-sm">{review.comment}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;